import React, { useState, useEffect, useCallback } from 'react';
import { expensesAPI, billingAPI } from '../services/api';
import ConfirmModal from './ConfirmModal';
import './ExpensesTab.css';

const ExpensesTab = ({ user }) => {
    const [activeTab, setActiveTab] = useState('invoices'); // invoices, expenses, config, quarterly
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    // Data State
    const [expenses, setExpenses] = useState([]);
    const [invoices, setInvoices] = useState([]); // Submitted invoices from therapists
    const [recurringExpenses, setRecurringExpenses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [therapists, setTherapists] = useState([]); // Needed for names in invoice list

    // Quarterly State
    const [selectedQuarter, setSelectedQuarter] = useState(1);
    const [quarterlyData, setQuarterlyData] = useState(null);

    // Modals
    const [showExpenseModal, setShowExpenseModal] = useState(false);
    const [expenseForm, setExpenseForm] = useState({ category: 'alquiler', description: '', amount: '', provider: '', date: '' });
    const [editingId, setEditingId] = useState(null);

    // Generic Modal State
    const [modalConfig, setModalConfig] = useState({
        isOpen: false,
        title: '',
        message: '',
        isPrompt: false,
        isDanger: false,
        onConfirm: () => { },
        inputPlaceholder: ''
    });

    // Helper: Month Options
    const months = [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];

    const fetchMonthlyData = useCallback(async () => {
        setLoading(true);
        try {
            // 1. Get Expenses
            const expensesRes = await expensesAPI.getAll({ month: selectedMonth + 1, year: selectedYear });
            setExpenses(Array.isArray(expensesRes.data) ? expensesRes.data : []);

            // 2. Get Invoices
            try {
                console.log(`Fetching invoices for ${selectedMonth}/${selectedYear}`);
                const invoicesRes = await billingAPI.getSubmissions({ month: selectedMonth, year: selectedYear });
                console.log('Invoices response:', invoicesRes.data);
                const invoicesList = Array.isArray(invoicesRes.data)
                    ? invoicesRes.data
                    : (Array.isArray(invoicesRes.data?.submissions) ? invoicesRes.data.submissions : []);
                setInvoices(invoicesList);
            } catch (error) {
                console.error('Error fetching invoices:', error);
                setInvoices([]);
            }

        } catch (error) {
            console.error('Error fetching data:', error);
            setExpenses([]);
            setInvoices([]);
        } finally {
            setLoading(false);
        }
    }, [selectedMonth, selectedYear]);

    // Initial Fetch of recurring
    useEffect(() => {
        const fetchRecurring = async () => {
            try {
                const res = await expensesAPI.getRecurring();
                setRecurringExpenses(Array.isArray(res.data) ? res.data : []);
            } catch (err) {
                console.error(err);
                setRecurringExpenses([]);
            }
        };
        fetchRecurring();
    }, []);

    useEffect(() => {
        fetchMonthlyData();
    }, [fetchMonthlyData]);

    // Fetch Quarterly Data on change
    useEffect(() => {
        if (activeTab === 'quarterly' && user?.role === 'admin') {
            const fetchQuarter = async () => {
                setLoading(true);
                try {
                    const res = await billingAPI.getQuarterlyReport({ year: selectedYear, quarter: selectedQuarter });
                    setQuarterlyData(res.data.data);
                } catch (err) {
                    console.error('Error fetching quarterly report', err);
                } finally {
                    setLoading(false);
                }
            };
            fetchQuarter();
        }
    }, [activeTab, selectedYear, selectedQuarter, user]);

    // Actions
    const handleCreateExpense = async (e) => {
        e.preventDefault();
        try {
            const data = {
                ...expenseForm,
                date: `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-01`
            };

            // 1. Create expense for current month
            await expensesAPI.create(data);

            // 2. If recurring is checked, create recurring template
            if (expenseForm.isRecurring) {
                await expensesAPI.createRecurring({
                    category: expenseForm.category,
                    description: expenseForm.description,
                    amount: expenseForm.amount,
                    provider: expenseForm.provider,
                    active: true
                });
            }

            setShowExpenseModal(false);
            fetchMonthlyData();
            // Reset form
            setExpenseForm({
                category: 'alquiler',
                description: '',
                amount: '',
                provider: '',
                date: '',
                isRecurring: false
            });
        } catch (err) {
            console.error(err);
        }
    };

    const handleDeleteExpense = (id) => {
        setModalConfig({
            isOpen: true,
            title: 'Eliminar Gasto',
            message: '¿Estás seguro de que deseas eliminar este gasto? Esta acción no se puede deshacer.',
            isDanger: true,
            onConfirm: async () => {
                await expensesAPI.delete(id);
                fetchMonthlyData();
                setModalConfig(prev => ({ ...prev, isOpen: false }));
            }
        });
    };

    const handleGenerateMonthly = () => {
        setModalConfig({
            isOpen: true,
            title: 'Generar Gastos Fijos',
            message: `¿Quieres importar todos los gastos recurrentes (fijos) a ${months[selectedMonth]}?`,
            onConfirm: async () => {
                try {
                    await expensesAPI.generateMonthly({ month: selectedMonth, year: selectedYear });
                    fetchMonthlyData();
                } catch (err) { console.error(err); }
                setModalConfig(prev => ({ ...prev, isOpen: false }));
            }
        });
    };

    const handleValidateInvoice = (submissionId, therapistId) => {
        setModalConfig({
            isOpen: true,
            title: 'Validar Factura',
            message: '¿Confirmas que esta factura está correcta y lista para pago?',
            confirmText: 'Validar',
            onConfirm: async () => {
                try {
                    await billingAPI.validateInvoice({
                        therapistId,
                        month: selectedMonth,
                        year: selectedYear,
                        validated: true,
                        paymentDate: new Date().toISOString().split('T')[0]
                    });
                    fetchMonthlyData();
                } catch (err) { console.error(err); }
                setModalConfig(prev => ({ ...prev, isOpen: false }));
            }
        });
    };

    const handleRevokeInvoice = (therapistId, submissionId) => {
        setModalConfig({
            isOpen: true,
            isPrompt: true,
            isDanger: true,
            title: 'Revocar Factura',
            message: 'Por favor, indica el motivo de la revocación. Se enviará una notificación al terapeuta.',
            inputPlaceholder: 'Ej: Falta incluir la sesión extra del día 15...',
            confirmText: 'Revocar',
            onConfirm: async (reason) => {
                try {
                    await billingAPI.revokeInvoice({
                        id: submissionId,
                        therapistId,
                        month: selectedMonth,
                        year: selectedYear,
                        reason
                    });
                    fetchMonthlyData();
                } catch (err) { console.error(err); }
                setModalConfig(prev => ({ ...prev, isOpen: false }));
            }
        });
    };

    const handleSaveQuarter = () => {
        if (!quarterlyData) return;
        setModalConfig({
            isOpen: true,
            title: 'Cerrar Trimestre',
            message: `¿Estás seguro de que deseas cerrar el T${selectedQuarter} de ${selectedYear}? Esto guardará los datos actuales y quedarán registrados en el histórico.`,
            confirmText: 'Guardar y Cerrar',
            onConfirm: async () => {
                try {
                    const res = await billingAPI.saveQuarterlyReport(quarterlyData);
                    setQuarterlyData(prev => ({ ...prev, ...res.data, status: 'closed', is_preview: false }));
                } catch (err) { console.error(err); }
                setModalConfig(prev => ({ ...prev, isOpen: false }));
            }
        });
    };


    // Summaries
    const summary = React.useMemo(() => {
        const safeExpenses = Array.isArray(expenses) ? expenses : [];
        const safeInvoices = Array.isArray(invoices) ? invoices : [];

        const totalExpenses = safeExpenses.reduce((sum, item) => sum + Number(item.amount || 0), 0); // Manual expenses (Rent, etc.)
        const totalInvoices = safeInvoices.reduce((sum, item) => sum + Number(item.total_amount || 0), 0); // What we pay to therapists

        // "Ingresos del Centro" comes from the 'center_amount' column in invoices (The % the center keeps)
        // If we want "Gross Income" (Subtotal), we can sum 'subtotal'.
        // But Net Profit is usually: (Center Income from Sessions) - (Fixed Expenses)
        const centerIncome = safeInvoices.reduce((sum, item) => sum + Number(item.center_amount || 0), 0);

        return {
            totalExpenses: totalExpenses,
            totalInvoices: totalInvoices,
            centerIncome: centerIncome,
            totalCombined: totalExpenses + totalInvoices, // Total OUT flow (Expenses + Salaries)
            netProfit: centerIncome - totalExpenses
        };
    }, [expenses, invoices]);

    // Helper for quarter labels
    const getQuarterMonths = (q) => {
        switch (q) {
            case 1: return 'Enero - Marzo';
            case 2: return 'Abril - Junio';
            case 3: return 'Julio - Septiembre';
            case 4: return 'Octubre - Diciembre';
            default: return '';
        }
    };

    return (
        <div className="expenses-container">
            <div className="expenses-header">
                <h2>Gestión Económica</h2>
                <div className="expenses-controls">
                    {activeTab !== 'quarterly' && (
                        <>
                            <select
                                className="month-selector"
                                value={selectedMonth}
                                onChange={e => setSelectedMonth(Number(e.target.value))}
                            >
                                {months.map((m, i) => <option key={i} value={i}>{m}</option>)}
                            </select>
                            <select
                                className="month-selector"
                                value={selectedYear}
                                onChange={e => setSelectedYear(Number(e.target.value))}
                            >
                                <option value={2024}>2024</option>
                                <option value={2025}>2025</option>
                                <option value={2026}>2026</option>
                            </select>
                        </>
                    )}
                </div>
            </div>

            <div className="expenses-tabs">
                <button
                    className={`tab-btn ${activeTab === 'invoices' ? 'active' : ''}`}
                    onClick={() => setActiveTab('invoices')}
                >
                    📥 Recepción Facturas
                </button>
                <button
                    className={`tab-btn ${activeTab === 'expenses' ? 'active' : ''}`}
                    onClick={() => setActiveTab('expenses')}
                >
                    💸 Gastos del Centro
                </button>
                <button
                    className={`tab-btn ${activeTab === 'config' ? 'active' : ''}`}
                    onClick={() => setActiveTab('config')}
                >
                    ⚙️ Configuración (Fijos)
                </button>
                {user?.role === 'admin' && (
                    <button
                        className={`tab-btn ${activeTab === 'quarterly' ? 'active' : ''}`}
                        onClick={() => setActiveTab('quarterly')}
                    >
                        📊 Trimestral
                    </button>
                )}
            </div>

            <div className="tab-content" style={{ marginTop: '20px' }}>

                {/* === TAB: INVOICES === */}
                {activeTab === 'invoices' && (
                    <div className="expenses-table-container">
                        <div className="table-header">
                            <h3>Facturas Presentadas por Terapeutas (Total a pagar: {summary.totalInvoices.toFixed(2)}€)</h3>
                        </div>
                        <table className="expenses-table">
                            <thead>
                                <tr>
                                    <th>Terapeuta</th>
                                    <th>Base</th>
                                    <th>% Centro</th>
                                    <th>Total A Pagar</th>
                                    <th>Estado</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(Array.isArray(invoices) ? invoices : []).map(inv => (
                                    <tr key={inv.id} className={`invoice-row ${inv.validated ? 'validated' : 'pending'}`}>
                                        <td style={{ fontWeight: 'bold' }}>{inv.therapist_name}</td>
                                        <td>{Number(inv.subtotal).toFixed(2)} €</td>
                                        <td style={{ color: '#2b8a3e' }}>+{Number(inv.center_amount).toFixed(2)} €</td>
                                        <td className="amount-col">{Number(inv.total_amount).toFixed(2)} €</td>
                                        <td>
                                            {inv.validated ? (
                                                <span className="status-badge badge-validated">PAGADO ({new Date(inv.payment_date).toLocaleDateString()})</span>
                                            ) : (
                                                <span className="status-badge badge-pending">PENDIENTE</span>
                                            )}
                                        </td>
                                        <td className="actions-cell">
                                            {!inv.validated && (
                                                <button className="btn-validate" onClick={() => handleValidateInvoice(inv.id, inv.therapist_id)}>
                                                    ✅ Validar
                                                </button>
                                            )}
                                            <button className="btn-revoke" onClick={() => handleRevokeInvoice(inv.therapist_id, inv.id)}>
                                                ↩️ Revocar
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {(!Array.isArray(invoices) || invoices.length === 0) && (
                                    <tr><td colSpan="6" style={{ textAlign: 'center', padding: '30px', color: '#999' }}>No se han presentado facturas para este mes.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}





                {activeTab === 'expenses' && (
                    <div className="tab-pane">
                        <div className="expenses-table-container">
                            <div className="table-header">
                                <h3>Gastos del Centro</h3>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <button className="btn-magic" onClick={handleGenerateMonthly}>
                                        ✨ Generar Fijos
                                    </button>
                                    <button className="btn-primary" onClick={() => setShowExpenseModal(true)}>
                                        + Nuevo Gasto
                                    </button>
                                </div>
                            </div>
                            <table className="expenses-table">
                                <thead>
                                    <tr>
                                        <th>Fecha</th>
                                        <th>Concepto</th>
                                        <th>Categoría</th>
                                        <th>Proveedor</th>
                                        <th>Importe</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(Array.isArray(expenses) ? expenses : []).map(expense => (
                                        <tr key={expense.id}>
                                            <td>{new Date(expense.date).toLocaleDateString()}</td>
                                            <td>{expense.description}</td>
                                            <td><span className={`category-badge cat-${expense.category}`}>{expense.category}</span></td>
                                            <td>{expense.provider}</td>
                                            <td className="amount-col">{Number(expense.amount).toFixed(2)} €</td>
                                            <td className="actions-cell">
                                                <button className="btn-action delete" onClick={() => handleDeleteExpense(expense.id)}>🗑️</button>
                                            </td>
                                        </tr>
                                    ))}
                                    {(!Array.isArray(expenses) || expenses.length === 0) && (
                                        <tr><td colSpan="6" style={{ textAlign: 'center', padding: '30px', color: '#999' }}>No hay gastos registrados este mes</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                    </div>
                )}

                {activeTab === 'config' && (
                    <div className="tab-pane">
                        <div className="expenses-table-container">
                            <div className="table-header">
                                <h3>Gastos Recurrentes (Plantillas)</h3>
                                <p style={{ fontSize: '0.85rem', color: '#666' }}>Estos gastos se copiarán automáticamente al usar "Generar Fijos".</p>
                            </div>
                            <table className="expenses-table">
                                <thead>
                                    <tr>
                                        <th>Categoría</th>
                                        <th>Concepto</th>
                                        <th>Importe Fijo</th>
                                        <th>Activo</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(Array.isArray(recurringExpenses) ? recurringExpenses : []).map(item => (
                                        <tr key={item.id}>
                                            <td><span className={`category-badge cat-${item.category}`}>{item.category}</span></td>
                                            <td>{item.description}</td>
                                            <td className="amount-col">{Number(item.amount).toFixed(2)} €</td>
                                            <td>{item.active ? '✅' : '❌'}</td>
                                        </tr>
                                    ))}
                                    {(!Array.isArray(recurringExpenses) || recurringExpenses.length === 0) && (
                                        <tr><td colSpan="4" style={{ textAlign: 'center', padding: '30px', color: '#999' }}>No hay gastos recurrentes configurados.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {activeTab === 'quarterly' && (
                    <div className="tab-pane">
                        <div className="filters-bar" style={{ marginBottom: '20px', display: 'flex', gap: '15px', alignItems: 'center' }}>
                            <label>Año:</label>
                            <select
                                className="month-selector"
                                value={selectedYear}
                                onChange={e => setSelectedYear(Number(e.target.value))}
                            >
                                <option value={2024}>2024</option>
                                <option value={2025}>2025</option>
                                <option value={2026}>2026</option>
                            </select>

                            <label>Trimestre:</label>
                            <div className="quarter-selector-wrapper" style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                <div className="quarter-selector">
                                    {[1, 2, 3, 4].map(q => (
                                        <button
                                            key={q}
                                            className={`quarter-btn ${selectedQuarter === q ? 'active' : ''}`}
                                            onClick={() => setSelectedQuarter(q)}
                                        >
                                            T{q}
                                        </button>
                                    ))}
                                </div>
                                <span style={{ fontSize: '0.8rem', color: '#666', fontStyle: 'italic', marginLeft: '2px' }}>
                                    {getQuarterMonths(selectedQuarter)}
                                </span>
                            </div>
                        </div>

                        {quarterlyData ? (
                            <>
                                {quarterlyData.status === 'closed' && (
                                    <div className="status-banner closed">
                                        🔒 Trimestre Cerrado (Guardado en Histórico)
                                    </div>
                                )}
                                {quarterlyData.status === 'draft' && (
                                    <div className="status-banner draft">
                                        👁️ Vista Previa (Datos en Vivo)
                                    </div>
                                )}

                                <div className="expenses-summary-cards quarterly-grid">
                                    <div className="summary-card total-revenue">
                                        <h3>Facturación Total</h3>
                                        <div className="summary-amount">{Number(quarterlyData.total_revenue).toFixed(2)} €</div>
                                        <div className="card-subtitle">Entrada Bruta</div>
                                    </div>

                                    <div className="summary-card therapists-payout">
                                        <h3>Parte Terapeutas</h3>
                                        <div className="summary-amount">{Number(quarterlyData.therapists_payout).toFixed(2)} €</div>
                                        <div className="card-subtitle">Pagos Realizados</div>
                                    </div>

                                    <div className="summary-card center-revenue">
                                        <h3>Parte del Centro</h3>
                                        <div className="summary-amount">{Number(quarterlyData.center_revenue).toFixed(2)} €</div>
                                        <div className="card-subtitle">Ingreso Bruto</div>
                                    </div>

                                    <div className="summary-card expenses">
                                        <h3>Total Gastos</h3>
                                        <div className="summary-amount" style={{ color: '#e03131' }}>-{Number(quarterlyData.expenses_total).toFixed(2)} €</div>
                                        <div className="card-subtitle">Gastos Operativos</div>
                                    </div>

                                    <div className="summary-card net-profit main-card">
                                        <h3>Beneficio Neto</h3>
                                        <div className={`summary-amount ${quarterlyData.net_profit >= 0 ? 'positive' : 'negative'}`}>
                                            {Number(quarterlyData.net_profit).toFixed(2)} €
                                        </div>
                                        <div className="card-subtitle">Resultado Final</div>
                                    </div>
                                </div>

                                {quarterlyData.status === 'draft' && (
                                    <div className="actions-footer" style={{ marginTop: '30px', textAlign: 'center' }}>
                                        <button className="btn-primary" onClick={handleSaveQuarter}>
                                            💾 Guardar y Cerrar Trimestre
                                        </button>
                                        <p style={{ marginTop: '10px', fontSize: '0.9rem', color: '#666' }}>
                                            Al guardar, se registrarán estos importes en el histórico y no cambiarán aunque se modifiquen facturas posteriormente.
                                        </p>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="loading-state">Cargando datos del trimestre...</div>
                        )}
                    </div>
                )}
            </div>

            {/* Add Expense Modal */}
            {
                showExpenseModal && (
                    <div className="modal-overlay">
                        <div className="modal-content">
                            <h3>Nuevo Gasto</h3>
                            <form onSubmit={handleCreateExpense}>
                                <div className="form-group">
                                    <label>Fecha</label>
                                    <input
                                        type="date"
                                        value={expenseForm.date}
                                        onChange={e => setExpenseForm({ ...expenseForm, date: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Categoría</label>
                                    <select
                                        value={expenseForm.category}
                                        onChange={e => setExpenseForm({ ...expenseForm, category: e.target.value })}
                                    >
                                        <option value="alquiler">Alquiler</option>
                                        <option value="suministros">Suministros (Luz, Agua, Internet)</option>
                                        <option value="autonomo">Cuota Autónomo</option>
                                        <option value="asesoria">Asesoría/Gestoría</option>
                                        <option value="publicidad">Publicidad/Marketing</option>
                                        <option value="material">Material Oficina</option>
                                        <option value="otros">Otros</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Concepto/Descripción</label>
                                    <input
                                        type="text"
                                        value={expenseForm.description}
                                        onChange={e => setExpenseForm({ ...expenseForm, description: e.target.value })}
                                        required
                                        placeholder="Ej: Alquiler Enero"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Proveedor (Opcional)</label>
                                    <input
                                        type="text"
                                        value={expenseForm.provider}
                                        onChange={e => setExpenseForm({ ...expenseForm, provider: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Importe (€)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={expenseForm.amount}
                                        onChange={e => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                                        required
                                    />
                                </div>

                                <div className="form-group checkbox">
                                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '10px', fontWeight: 'bold', color: '#2b8a3e' }}>
                                        <input
                                            type="checkbox"
                                            checked={expenseForm.isRecurring || false}
                                            onChange={e => setExpenseForm({ ...expenseForm, isRecurring: e.target.checked })}
                                            style={{ width: '20px', height: '20px' }}
                                        />
                                        🔄 Es un gasto fijo mensual (Guardar plantilla)
                                    </label>
                                </div>

                                <div className="modal-actions">
                                    <button type="button" className="btn-cancel" onClick={() => setShowExpenseModal(false)}>Cancelar</button>
                                    <button type="submit" className="btn-primary">Guardar Gasto</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            }

            <ConfirmModal
                isOpen={modalConfig.isOpen}
                onClose={() => setModalConfig(prev => ({ ...prev, isOpen: false }))}
                onConfirm={modalConfig.onConfirm}
                title={modalConfig.title}
                message={modalConfig.message}
                isPrompt={modalConfig.isPrompt}
                isDanger={modalConfig.isDanger}
                confirmText={modalConfig.confirmText}
                inputPlaceholder={modalConfig.inputPlaceholder}
            />

        </div >
    );
};

export default ExpensesTab;
